import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { AliasNavigate } from "./Components/AliasNavigate";
import { AliasLoginGate } from "./Components/AliasLoginGate";
import {
  legacyAppPathToPrefixed,
  loginPath,
  readLastAlias,
} from "./utils/contractPath";
import "./App.css";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { NavProvider } from "./context/NavContext";
import { ProtectedRoute } from "./Components/ProtectedRoute";
import { ROUTE_AUTH, ROUTE_SUBMODULE } from "./utils/routeAuth";

const Home = lazy(() => import("./Components/Home/Home"));
const EsqueceuSenha = lazy(() => import("./Components/Login/EsqueceuSenha"));
const RedefinirSenha = lazy(() => import("./Components/Login/RedefinirSenha"));
const RootLanding = lazy(() => import("./Components/RootLanding/RootLanding"));
const Usuarios = lazy(() => import("./Components/Usuarios/Usuarios"));
const UsuarioHistorico = lazy(() => import("./Components/Usuarios/UsuarioHistorico"));
const GruposUsuarios = lazy(() => import("./Components/GruposUsuarios/GruposUsuarios"));
const PerfisAcesso = lazy(() => import("./Components/PerfisAcesso/PerfisAcesso"));
const FuncoesAcesso = lazy(() => import("./Components/FuncoesAcesso/FuncoesAcesso"));
const AcessosCompanhia = lazy(() => import("./Components/AcessosCompanhia/AcessosCompanhia"));
const AcessoPortal = lazy(() => import("./Components/AcessoPortal/AcessoPortal"));
const GrupoUsuarioHistorico = lazy(() => import("./Components/GruposUsuarios/GrupoUsuarioHistorico"));
const Entidades = lazy(() => import("./Components/Entidades/Entidades"));
const Companhias = lazy(() => import("./Components/Cad/Companhias"));
const Contrapartes = lazy(() => import("./Components/Cad/Contrapartes"));
const Clientes = lazy(() => import("./Components/Cad/Clientes"));
const Inquilinos = lazy(() => import("./Components/Cad/Inquilinos"));
const Socios = lazy(() => import("./Components/Cad/Socios"));
const ContratosLocacao = lazy(() => import("./Components/Cad/ContratosLocacao"));
const AdmClientes = lazy(() => import("./Components/Adm/Clientes/AdmClientes"));
const AdmModulosCliente = lazy(() => import("./Components/Adm/ModulosCliente/AdmModulosCliente"));
const AdmBancosCliente = lazy(() => import("./Components/Adm/Bancos/AdmBancosCliente"));
const AdmAdministradorCliente = lazy(() => import("./Components/Adm/Administrador/AdmAdministradorCliente"));
const AdmAssinatura = lazy(() => import("./Components/Adm/Assinatura/AdmAssinatura"));
const AdmConfiguracao = lazy(() => import("./Components/Adm/Configuracao/AdmConfiguracao"));
const AdmEmail = lazy(() => import("./Components/Adm/Email/AdmEmail"));
const AdmDocStorage = lazy(
  () => import("./Components/Adm/DocStorage/AdmDocStorage")
);
const AdmSeguranca = lazy(() => import("./Components/Adm/Seguranca/AdmSeguranca"));
const EntidadeHistorico = lazy(() => import("./Components/Entidades/EntidadeHistorico"));
const Eventos = lazy(() => import("./Components/Ctb/cadastro/Eventos"));
const GradeContabil = lazy(() => import("./Components/Ctb/cadastro/GradeContabil"));
const CentrosCusto = lazy(() => import("./Components/Gsf/CentrosCusto"));
const Operacoes = lazy(() => import("./Components/Ctb/cadastro/Operacoes"));
const PlanoContas = lazy(() => import("./Components/Ctb/cadastro/PlanoContas"));
const ContasAPagar = lazy(() => import("./Components/Gsf/ContasAPagar"));
const BaixasPendentesContasAPagar = lazy(() => import("./Components/Gsf/BaixasPendentesContasAPagar"));
const BaixasPendentesContasAReceber = lazy(() => import("./Components/Gsf/BaixasPendentesContasAReceber"));
const TituloContaPagar = lazy(() => import("./Components/Gsf/TituloContaPagar"));
const ContasAReceberPage = lazy(() => import("./Components/Gsf/ContasAReceber"));
const TituloContaReceber = lazy(() => import("./Components/Gsf/TituloContaReceber"));
const LivroCaixa = lazy(() => import("./Components/Gsf/LivroCaixa"));
const TransferenciaTesouraria = lazy(() => import("./Components/Gsf/TransferenciaTesouraria"));
const LancamentoExtratos = lazy(() => import("./Components/Gsf/LancamentoExtratos"));
const ExtratoImobiliariaForm = lazy(() => import("./Components/Gsf/ExtratoImobiliariaForm"));
const BookIncomeExpensesReport = lazy(() => import("./Components/Ctb/BookIncomeExpensesReport"));
const BookBalanceSheetReport = lazy(() => import("./Components/Ctb/BookBalanceSheetReport"));
const CtbReports = lazy(() => import("./Components/Ctb/reports/CtbReports"));
const LivroContaCorrente = lazy(() => import("./Components/Gsf/LivroContaCorrente"));
const ConciliacaoBancaria = lazy(() => import("./Components/Gsf/ConciliacaoBancaria"));
const FechamentoCaixa = lazy(() => import("./Components/Gsf/FechamentoCaixa"));
const Deposito = lazy(() => import("./Components/Gsf/Deposito"));
const ListaDeDepositos = lazy(() => import("./Components/Gsf/ListaDeDepositos"));
const LancamentoContaCorrente = lazy(() => import("./Components/Gsf/LancamentoContaCorrente"));
const MovimentacaoSocios = lazy(() => import("./Components/Gsf/MovimentacaoSocios"));
const EventosFinanceiros = lazy(() => import("./Components/Cad/EventosFinanceiros"));
const Propriedades = lazy(() => import("./Components/Cad/Propriedades"));
const FinancialEvents = lazy(() => import("./Components/Cad/FinancialEvents"));
const InterfaceContexts = lazy(() => import("./Components/Cad/InterfaceContexts"));
const ConfiguracaoRelatorios = lazy(() => import("./Components/Cad/ConfiguracaoRelatorios"));
const ConfiguracaoWorkflow = lazy(() => import("./Components/Cad/ConfiguracaoWorkflow"));
const DocTemplates = lazy(() => import("./Components/Cad/DocTemplates"));
const DocNumbering = lazy(() => import("./Components/Cad/DocNumbering"));
const DocTriggerOps = lazy(() => import("./Components/Cad/DocTriggerOps"));
const CadRepositorio = lazy(() => import("./Components/Cad/CadRepositorio"));
const Documentos = lazy(() => import("./Components/Gsf/Documentos"));
const AuditoriaDespesasRecorrentes = lazy(
  () => import("./Components/Gsf/AuditoriaDespesasRecorrentes")
);
const IntegracaoContabil = lazy(() => import("./Components/Ctb/IntegracaoContabil"));
const FechamentoContabil = lazy(() => import("./Components/Ctb/FechamentoContabil"));
const LancamentoContabilManual = lazy(() => import("./Components/Ctb/LancamentoContabilManual"));
const AdiantamentoFornecedor = lazy(() => import("./Components/Gsf/AdiantamentoFornecedor"));
const AdiantamentoCliente = lazy(() => import("./Components/Gsf/AdiantamentoCliente"));
const PortalPerfil = lazy(() => import("./Components/Portal/Perfil"));
const PortalPropriedades = lazy(() => import("./Components/Portal/Propriedades"));
const PortalExtratos = lazy(() => import("./Components/Portal/Extratos"));

function RedirectTituloCadastroId() {
  const { id } = useParams();
  return <AliasNavigate to={`/gsf/contas-a-pagar/lancamento/${id}`} />;
}

function RedirectLegacyLeFormId() {
  const { id } = useParams();
  return <AliasNavigate to={`/gsf/extratos-proprietario/${id}`} />;
}

function RedirectEventGrade() {
  const { eventId } = useParams();
  return <AliasNavigate to={`/ctb/eventos/${eventId}/grade`} />;
}

/** Paths antigos sem alias (/inicio, /gsf/…) → /{alias}/… */
function LegacyAppRedirect() {
  const location = useLocation();
  const { user } = useAuth();
  const target = legacyAppPathToPrefixed(
    location.pathname,
    location.search,
    location.hash,
    user?.cd_alias
  );
  if (target) return <Navigate to={target} replace />;
  return <Navigate to="/" replace />;
}

function LoginWithoutAlias() {
  const alias = readLastAlias();
  if (alias) return <Navigate to={loginPath(alias)} replace />;
  return <Navigate to="/" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
      <NavProvider>
      <div className="App">
        <Suspense fallback={<div className="App-route-loading">A carregar…</div>}>
        <Routes>
          <Route path="/" element={<RootLanding />} />
          <Route path="/login" element={<LoginWithoutAlias />} />
          <Route path="/:cdAlias/esqueceu-senha" element={<EsqueceuSenha />} />
          <Route path="/:cdAlias/redefinir-senha" element={<RedefinirSenha />} />
          <Route
            path="/:cdAlias/inicio"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/usuarios"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.SCA_USER} requireSubmodule={ROUTE_SUBMODULE.SCA_USER}>
                <Usuarios />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/usuarios/historico/:userId"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.SCA_USER_HIST} requireSubmodule={ROUTE_SUBMODULE.SCA_USER_HIST}>
                <UsuarioHistorico />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/grupos-usuarios"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.SCA_USERGROUP} requireSubmodule={ROUTE_SUBMODULE.SCA_USERGROUP}>
                <GruposUsuarios />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/perfis-acesso"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.SCA_PROFILE} requireSubmodule={ROUTE_SUBMODULE.SCA_PROFILE}>
                <PerfisAcesso />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/funcoes-acesso"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.SCA_FUNCTION} requireSubmodule={ROUTE_SUBMODULE.SCA_FUNCTION}>
                <FuncoesAcesso />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/acessos-companhia"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.SCA_COMPANY_ACCESS} requireSubmodule={ROUTE_SUBMODULE.SCA_COMPANY_ACCESS}>
                <AcessosCompanhia />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/acesso-portal"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.SCA_COMPANY_ACCESS} requireSubmodule={ROUTE_SUBMODULE.SCA_PORTAL_ACCESS}>
                <AcessoPortal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/grupos-usuarios/historico/:usergroupId"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.SCA_USERGROUP_HIST} requireSubmodule={ROUTE_SUBMODULE.SCA_USERGROUP_HIST}>
                <GrupoUsuarioHistorico />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/entidades"
            element={
              <ProtectedRoute
                requireAuth={[
                  ROUTE_AUTH.CAD_COMPANY,
                  ROUTE_AUTH.CAD_COUNTERPARTY,
                  ROUTE_AUTH.CAD_CUSTOMER,
                  ROUTE_AUTH.CAD_TENANT,
                  ROUTE_AUTH.CAD_MEMBER,
                ]}
                requireActiveCompany
              >
                <Entidades />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/companhias"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CAD_COMPANY} requireSubmodule={ROUTE_SUBMODULE.CAD_COMPANY} requireActiveCompany>
                <Companhias />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/socios"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CAD_MEMBER} requireSubmodule={ROUTE_SUBMODULE.CAD_MEMBER} requireActiveCompany>
                <Socios />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/contrapartes"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CAD_COUNTERPARTY} requireSubmodule={ROUTE_SUBMODULE.CAD_COUNTERPARTY} requireActiveCompany>
                <Contrapartes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/clientes"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CAD_CUSTOMER} requireSubmodule={ROUTE_SUBMODULE.CAD_CUSTOMER} requireActiveCompany>
                <Clientes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/inquilinos"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CAD_TENANT} requireSubmodule={ROUTE_SUBMODULE.CAD_TENANT} requireActiveCompany>
                <Inquilinos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/adm/clientes"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.ADM_CLIENT} requireModule="ADM">
                <AdmClientes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/adm/clientes/:idClient/modulos"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.ADM_CLIENT} requireModule="ADM">
                <AdmModulosCliente />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/adm/clientes/:idClient/bancos"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.ADM_CLIENT} requireModule="ADM">
                <AdmBancosCliente />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/adm/clientes/:idClient/administrador"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.ADM_CLIENT} requireModule="ADM">
                <AdmAdministradorCliente />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/adm/configuracao"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.ADM_CONFIG} requireModule="ADM">
                <AdmConfiguracao />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/adm/email"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.ADM_EMAIL} requireModule="ADM">
                <AdmEmail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/adm/repositorio-documentos"
            element={
              <ProtectedRoute
                requireAuth={ROUTE_AUTH.ADM_DOC_STORAGE}
                requireModule="ADM"
              >
                <AdmDocStorage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/adm/assinatura"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.ADM_ASSINATURA} requireModule="ADM">
                <AdmAssinatura />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/adm/seguranca"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.ADM_SEGURANCA} requireModule="ADM">
                <AdmSeguranca />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/entidades/historico/:entityId"
            element={
              <ProtectedRoute
                requireAuth={[
                  ROUTE_AUTH.CAD_COMPANY,
                  ROUTE_AUTH.CAD_COUNTERPARTY,
                  ROUTE_AUTH.CAD_CUSTOMER,
                  ROUTE_AUTH.CAD_TENANT,
                  ROUTE_AUTH.CAD_MEMBER,
                ]}
                requireActiveCompany
              >
                <EntidadeHistorico />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/ctb/eventos"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CTB_EVENT} requireSubmodule={ROUTE_SUBMODULE.CTB_EVENT} requireActiveCompany>
                <Eventos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/ctb/eventos/:eventId/grade"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CTB_EVENT} requireSubmodule={ROUTE_SUBMODULE.CTB_EVENT} requireActiveCompany>
                <GradeContabil />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/ctb/operacoes"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CTB_OPERATION} requireSubmodule={ROUTE_SUBMODULE.CTB_OPERATION} requireActiveCompany>
                <Operacoes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/ctb/plano-contas"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CTB_COA} requireSubmodule={ROUTE_SUBMODULE.CTB_COA} requireActiveCompany>
                <PlanoContas />
              </ProtectedRoute>
            }
          />
          <Route path="/:cdAlias/cad/eventos" element={<AliasNavigate to="/ctb/eventos" />} />
          <Route
            path="/:cdAlias/cad/eventos/:eventId/grade"
            element={<RedirectEventGrade />}
          />
          <Route path="/:cdAlias/cad/operacoes" element={<AliasNavigate to="/ctb/operacoes" />} />
          <Route path="/:cdAlias/cad/plano-contas" element={<AliasNavigate to="/ctb/plano-contas" />} />
          <Route path="/:cdAlias/cad/transacoes" element={<AliasNavigate to="/ctb/operacoes" />} />
          <Route
            path="/:cdAlias/cad/centros-custo"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CAD_COST_CENTER} requireSubmodule={ROUTE_SUBMODULE.CAD_COST_CENTER} requireActiveCompany>
                <CentrosCusto />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/propriedades"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CAD_PROPERTY} requireSubmodule={ROUTE_SUBMODULE.CAD_PROPERTY} requireActiveCompany>
                <Propriedades />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/contratos-locacao"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CAD_LEASE} requireSubmodule={ROUTE_SUBMODULE.CAD_LEASE} requireActiveCompany>
                <ContratosLocacao />
              </ProtectedRoute>
            }
          />
          <Route path="/:cdAlias/gsf/eventos" element={<AliasNavigate to="/ctb/eventos" />} />
          <Route path="/:cdAlias/gsf/operacoes" element={<AliasNavigate to="/ctb/operacoes" />} />
          <Route path="/:cdAlias/gsf/plano-contas" element={<AliasNavigate to="/ctb/plano-contas" />} />
          <Route path="/:cdAlias/gsf/centros-custo" element={<AliasNavigate to="/cad/centros-custo" />} />
          <Route path="/:cdAlias/gsf/transacoes" element={<AliasNavigate to="/ctb/operacoes" />} />
          <Route
            path="/:cdAlias/gsf/contas-a-pagar/lancamento/:id"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_AP} requireSubmodule={ROUTE_SUBMODULE.GSF_AP} requireActiveCompany>
                <TituloContaPagar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/contas-a-pagar/lancamento"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_AP} requireSubmodule={ROUTE_SUBMODULE.GSF_AP} requireActiveCompany>
                <TituloContaPagar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/contas-a-pagar/baixas-pendentes"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_AP} requireSubmodule={ROUTE_SUBMODULE.GSF_AP} requireActiveCompany>
                <BaixasPendentesContasAPagar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/contas-a-pagar"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_AP} requireSubmodule={ROUTE_SUBMODULE.GSF_AP} requireActiveCompany>
                <ContasAPagar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/contas-a-receber/recebimentos-pendentes"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_AR} requireSubmodule={ROUTE_SUBMODULE.GSF_AR} requireActiveCompany>
                <BaixasPendentesContasAReceber />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/contas-a-receber/lancamento/:id"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_AR} requireSubmodule={ROUTE_SUBMODULE.GSF_AR} requireActiveCompany>
                <TituloContaReceber />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/contas-a-receber/lancamento"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_AR} requireSubmodule={ROUTE_SUBMODULE.GSF_AR} requireActiveCompany>
                <TituloContaReceber />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/contas-a-receber"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_AR} requireSubmodule={ROUTE_SUBMODULE.GSF_AR} requireActiveCompany>
                <ContasAReceberPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/livro-caixa/transferencia"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_CASH_BOOK} requireSubmodule={ROUTE_SUBMODULE.GSF_CASH_BOOK} requireActiveCompany>
                <TransferenciaTesouraria />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/extratos-proprietario/novo"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_STATEMENT_OWN} requireSubmodule={ROUTE_SUBMODULE.GSF_STATEMENT} requireActiveCompany>
                <ExtratoImobiliariaForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/extratos-proprietario/:id"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_STATEMENT_OWN} requireSubmodule={ROUTE_SUBMODULE.GSF_STATEMENT} requireActiveCompany>
                <ExtratoImobiliariaForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/extratos-proprietario"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_STATEMENT_OWN} requireSubmodule={ROUTE_SUBMODULE.GSF_STATEMENT} requireActiveCompany>
                <LancamentoExtratos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/extratos-administracao/novo"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_STATEMENT_ADMIN} requireSubmodule={ROUTE_SUBMODULE.GSF_STATEMENT} requireActiveCompany>
                <ExtratoImobiliariaForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/extratos-administracao/:id"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_STATEMENT_ADMIN} requireSubmodule={ROUTE_SUBMODULE.GSF_STATEMENT} requireActiveCompany>
                <ExtratoImobiliariaForm />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/extratos-administracao"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_STATEMENT_ADMIN} requireSubmodule={ROUTE_SUBMODULE.GSF_STATEMENT} requireActiveCompany>
                <LancamentoExtratos />
              </ProtectedRoute>
            }
          />
          {/* Legado → Extratos Proprietário (form corrige se ADMIN) */}
          <Route
            path="/:cdAlias/gsf/lancamento-extratos/novo"
            element={<AliasNavigate to="/gsf/extratos-proprietario/novo" />}
          />
          <Route
            path="/:cdAlias/gsf/lancamento-extratos/:id"
            element={<RedirectLegacyLeFormId />}
          />
          <Route
            path="/:cdAlias/gsf/lancamento-extratos"
            element={<AliasNavigate to="/gsf/extratos-proprietario" />}
          />
          <Route
            path="/:cdAlias/gsf/livro-caixa"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_CASH_BOOK} requireSubmodule={ROUTE_SUBMODULE.GSF_CASH_BOOK} requireActiveCompany>
                <LivroCaixa />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/contas-financeiras"
            element={<AliasNavigate to="/entidades" />}
          />
          <Route
            path="/:cdAlias/gsf/livro-conta-corrente/lancamento"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_BANK_LEDGER} requireSubmodule={ROUTE_SUBMODULE.GSF_BANK_LEDGER} requireActiveCompany>
                <LancamentoContaCorrente />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/livro-conta-corrente"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_BANK_LEDGER} requireSubmodule={ROUTE_SUBMODULE.GSF_BANK_LEDGER} requireActiveCompany>
                <LivroContaCorrente />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/conciliacao-bancaria"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_BANK_RECON} requireSubmodule={ROUTE_SUBMODULE.GSF_BANK_RECON} requireActiveCompany>
                <ConciliacaoBancaria />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/fechamento-caixa/deposito/:id"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_FCX} requireSubmodule={ROUTE_SUBMODULE.GSF_FCX} requireActiveCompany>
                <Deposito />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/fechamento-caixa/deposito"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_FCX} requireSubmodule={ROUTE_SUBMODULE.GSF_FCX} requireActiveCompany>
                <Deposito />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/fechamento-caixa/lista-de-depositos"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_FCX} requireSubmodule={ROUTE_SUBMODULE.GSF_FCX} requireActiveCompany>
                <ListaDeDepositos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/fechamento-caixa"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_FCX} requireSubmodule={ROUTE_SUBMODULE.GSF_FCX} requireActiveCompany>
                <FechamentoCaixa />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/extrato-bancario"
            element={<AliasNavigate to="/gsf/livro-conta-corrente" />}
          />
          <Route
            path="/:cdAlias/gsf/movimentacao-socios"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_MEMBER_EQUITY} requireSubmodule={ROUTE_SUBMODULE.GSF_MEMBER_EQUITY}>
                <MovimentacaoSocios />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/member-equity-entries"
            element={<AliasNavigate to="/gsf/movimentacao-socios" />}
          />
          <Route
            path="/:cdAlias/gsf/financial-transactions"
            element={<AliasNavigate to="/gsf/movimentacao-socios" />}
          />
          <Route
            path="/:cdAlias/cad/financial-operations"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CAD_FIN_OPERATION} requireSubmodule={ROUTE_SUBMODULE.CAD_FIN_OPERATION} requireActiveCompany>
                <EventosFinanceiros />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/operacoes-financeiras"
            element={<AliasNavigate to="/cad/financial-operations" />}
          />
          <Route
            path="/:cdAlias/cad/transaction-types"
            element={<AliasNavigate to="/cad/financial-operations" />}
          />
          <Route
            path="/:cdAlias/cad/financial-events"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CAD_FIN_EVENT} requireSubmodule={ROUTE_SUBMODULE.CAD_FIN_EVENT} requireActiveCompany>
                <FinancialEvents />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/accounting-contexts"
            element={<AliasNavigate to="/cad/interface-contexts" />}
          />
          <Route
            path="/:cdAlias/cad/interface-contexts"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CAD_IFACE_CONTEXT} requireSubmodule={ROUTE_SUBMODULE.CAD_IFACE_CONTEXT} requireActiveCompany>
                <InterfaceContexts />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/configuracao-relatorios"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.TECH_RPT_CONFIG} requireSubmodule={ROUTE_SUBMODULE.TECH_RPT_CONFIG} requireActiveCompany>
                <ConfiguracaoRelatorios />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/configuracao-workflow"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.TECH_WF_CONFIG} requireSubmodule={ROUTE_SUBMODULE.TECH_WF_CONFIG} requireActiveCompany>
                <ConfiguracaoWorkflow />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/templates-documento"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.TECH_DOC_TEMPLATE} requireSubmodule={ROUTE_SUBMODULE.TECH_DOC_TEMPLATE} requireActiveCompany>
                <DocTemplates />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/numeracao-documentos"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.TECH_DOC_NUMBERING} requireSubmodule={ROUTE_SUBMODULE.TECH_DOC_NUMBERING} requireActiveCompany>
                <DocNumbering />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/automacao-servicos"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.TECH_DOC_TRIGGER} requireSubmodule={ROUTE_SUBMODULE.TECH_DOC_TRIGGER} requireActiveCompany>
                <DocTriggerOps />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/configuracao-repositorio"
            element={
              <ProtectedRoute
                requireAuth={ROUTE_AUTH.TECH_REPO_CONFIG}
                requireSubmodule={ROUTE_SUBMODULE.TECH_REPO_CONFIG}
                requireActiveCompany
              >
                <CadRepositorio />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/configuracao-repositorios"
            element={<AliasNavigate to="/cad/configuracao-repositorio" />}
          />
          <Route
            path="/:cdAlias/gsf/documentos"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_DOC_OUTBOX} requireSubmodule={ROUTE_SUBMODULE.GSF_DOC_OUTBOX} requireActiveCompany>
                <Documentos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/auditoria-despesas-recorrentes"
            element={
              <ProtectedRoute
                requireAuth={ROUTE_AUTH.GSF_RECURRING_EXPENSE_AUDIT}
                requireSubmodule={ROUTE_SUBMODULE.GSF_RECURRING_EXPENSE_AUDIT}
                requireActiveCompany
              >
                <AuditoriaDespesasRecorrentes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/cad/transaction-groups"
            element={<AliasNavigate to="/cad/financial-events" />}
          />
          <Route
            path="/:cdAlias/cad/financial-event-groups"
            element={<AliasNavigate to="/cad/financial-events" />}
          />
          <Route
            path="/:cdAlias/gsf/financial-events"
            element={<AliasNavigate to="/cad/financial-events" />}
          />
          <Route
            path="/:cdAlias/gsf/event-groups"
            element={<AliasNavigate to="/cad/financial-events" />}
          />
          <Route
            path="/:cdAlias/financeiro/livro-conta-corrente"
            element={<AliasNavigate to="/gsf/livro-conta-corrente" />}
          />
          <Route
            path="/:cdAlias/financeiro/extrato-bancario"
            element={<AliasNavigate to="/gsf/livro-conta-corrente" />}
          />
          <Route
            path="/:cdAlias/ctb/reports"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CTB_REPORT} requireSubmodule={ROUTE_SUBMODULE.CTB_REPORT} requireActiveCompany>
                <CtbReports />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/ctb/book-income-expenses"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CTB_REPORT} requireSubmodule={ROUTE_SUBMODULE.CTB_REPORT} requireActiveCompany>
                <BookIncomeExpensesReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/ctb/book-balance-sheet"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CTB_REPORT} requireSubmodule={ROUTE_SUBMODULE.CTB_REPORT} requireActiveCompany>
                <BookBalanceSheetReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/ctb/integracao"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CTB_INTEGRATION} requireSubmodule={ROUTE_SUBMODULE.CTB_INTEGRATION} requireActiveCompany>
                <IntegracaoContabil />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/adiantamento-fornecedor"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_VENDOR_ADV} requireSubmodule={ROUTE_SUBMODULE.GSF_VENDOR_ADV} requireActiveCompany>
                <AdiantamentoFornecedor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/adiantamento-cliente"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_CUSTOMER_ADV} requireSubmodule={ROUTE_SUBMODULE.GSF_CUSTOMER_ADV} requireActiveCompany>
                <AdiantamentoCliente />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/portal/perfil"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.PORTAL_PROFILE} requireSubmodule={ROUTE_SUBMODULE.PORTAL_PROFILE} requireActiveCompany>
                <PortalPerfil />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/portal/propriedades"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.PORTAL_PROPERTY} requireSubmodule={ROUTE_SUBMODULE.PORTAL_PROPERTY} requireActiveCompany>
                <PortalPropriedades />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/portal/extratos"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.PORTAL_STATEMENT} requireSubmodule={ROUTE_SUBMODULE.PORTAL_STATEMENT} requireActiveCompany>
                <PortalExtratos />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/ctb/lancamento-manual"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CTB_MANUAL_JE} requireSubmodule={ROUTE_SUBMODULE.CTB_MANUAL_JE} requireActiveCompany>
                <LancamentoContabilManual />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/ctb/fechamento"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.CTB_CLOSING} requireSubmodule={ROUTE_SUBMODULE.CTB_CLOSING} requireActiveCompany>
                <FechamentoContabil />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/titulos/cadastro/:id"
            element={
              <ProtectedRoute requireAuth={ROUTE_AUTH.GSF_AP} requireSubmodule={ROUTE_SUBMODULE.GSF_AP}>
                <RedirectTituloCadastroId />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:cdAlias/gsf/titulos/cadastro"
            element={
              <AliasNavigate to="/gsf/contas-a-pagar/lancamento" />
            }
          />
          <Route
            path="/:cdAlias/gsf/titulos"
            element={<AliasNavigate to="/gsf/contas-a-pagar" />}
          />
          <Route path="/:cdAlias" element={<AliasLoginGate />} />
          <Route path="*" element={<LegacyAppRedirect />} />
        </Routes>
        </Suspense>
      </div>
      </NavProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
